// Helpers de mapeamento entre URL ↔ view state interno do App.
// O view state continua sendo a fonte de renderização; URL espelha pra:
//   - botão voltar do navegador funcionar
//   - links compartilháveis (ex: forjamente.com.br/grupo/abc)
//   - F5 manter usuário na mesma tela
//   - Analytics rastrear page views por seção
//
// Convenção: rotas em pt-BR pra parecerem orgânicas no produto.
// "/grupo/:id" porque o termo padrão do app é Clube/Grupo (Wesley pode escolher).

import { BookV2 } from './types';

export type Tab = 'home' | 'clubs' | 'stats' | 'explore' | 'me';

// Reescrita do tipo View pra incluir os IDs/dados que precisam vir da URL.
// Quando a URL é fonte da verdade (F5, deep link), o componente lookup pelo ID
// no state global (books, clubs) — se não encontrar, dispara fetch.
export type View =
  | { kind: 'tab' }
  | { kind: 'book'; book: BookV2; bookId?: string }
  | { kind: 'book-study'; book: BookV2; bookId?: string; composePage?: number | null }
  | { kind: 'travessia'; book: BookV2; bookId?: string; item?: string }
  | { kind: 'estudos' }
  | { kind: 'cadernos' }
  | { kind: 'notif' }
  | { kind: 'report' }
  | { kind: 'admin' }
  | { kind: 'subscription' }
  | { kind: 'readings' }
  | { kind: 'library' }
  | { kind: 'club-explore' }
  | { kind: 'club-inner'; clubId: string }
  | { kind: 'proposta'; clubId: string }
  | { kind: 'club-create'; propose?: boolean }
  | { kind: 'club-empty'; clubData: any; clubId: string | null }
  | { kind: 'club-analytics'; clubId: string }
  | { kind: 'club-question-box'; clubId: string; initialPhase?: 'create' | 'active'; postId?: string }
  | { kind: 'estudo'; clubId: string; bookId?: string; focusPostId?: string }
  | { kind: 'insight-detail'; insight: any; clubId: string; postId?: string }
  | { kind: 'user'; userId: string }
  | { kind: 'settings' }
  | { kind: 'tutoriais' }
  // Protocolo do Habito — a area de estudo do programa.
  | { kind: 'programa' }
  // Círculo do Mentor — a página de um Mentor (/mentor/wesley).
  | { kind: 'mentor'; slug: string }
  // "Meus episódios" — o Mentor sobe e publica os episódios (/mentor/wesley/estudio).
  | { kind: 'mentorEstudio'; slug: string }
  // O Editor do Mentor — perfil, página, série e clube (/circulo/editor).
  | { kind: 'circuloEditor'; vista?: 'serie' };

// Computa a URL canônica de uma view+tab. Usado pelos handlers ao navegar.
export function viewToUrl(view: View, tab: Tab): string {
  switch (view.kind) {
    case 'tab':
      if (tab === 'home')  return '/';
      if (tab === 'clubs') return '/grupos';
      if (tab === 'stats') return '/ritmo';
      if (tab === 'explore') return '/explorar';
      if (tab === 'me')    return '/eu';
      return '/';
    case 'library':         return '/biblioteca';
    case 'report':          return '/relatorio';
    case 'notif':           return '/notificacoes';
    case 'admin':           return '/admin';
    case 'subscription':    return '/assinatura';
    case 'readings':        return '/leituras';
    case 'book':
      return `/livro/${(view as any).book?.id || (view as any).bookId || ''}`;
    case 'estudos':        return '/estudos';
    case 'cadernos':       return '/cadernos';
    case 'book-study':
      return `/livro/${(view as any).book?.id || (view as any).bookId || ''}/caderno`;
    case 'travessia':
      return `/livro/${(view as any).book?.id || (view as any).bookId || ''}/travessia`;
    case 'club-explore':    return '/explorar';
    case 'club-create':     return '/grupos/criar';
    case 'club-empty':      return '/grupos/recem-criado';
    case 'club-inner':      return `/grupo/${view.clubId}`;
    case 'proposta':        return `/proposta/${view.clubId}`;
    case 'estudo':          return `/grupo/${view.clubId}/estudo`;
    case 'club-analytics':  return `/grupo/${view.clubId}/painel`;
    case 'club-question-box':
      // Modo active de uma Caixa específica: /grupo/:id/perguntas/:postId
      if (view.postId) return `/grupo/${view.clubId}/perguntas/${view.postId}`;
      return `/grupo/${view.clubId}/perguntas${view.initialPhase === 'create' ? '/criar' : ''}`;
    case 'insight-detail':
      return `/grupo/${view.clubId}/insight/${view.postId || (view.insight && view.insight.id) || ''}`;
    case 'user':            return `/u/${(view as any).userId}`;
    case 'settings':        return '/ajustes';
    case 'tutoriais':       return '/tutoriais';
    case 'programa':        return '/programa';
    case 'mentor':          return `/mentor/${encodeURIComponent((view as any).slug || '')}`;
    case 'mentorEstudio':   return `/mentor/${encodeURIComponent((view as any).slug || '')}/estudio`;
    case 'circuloEditor':   return (view as any).vista === 'serie' ? '/circulo/editor?vista=serie' : '/circulo/editor';
    default: return '/';
  }
}

// Computa view+tab a partir da URL atual. Usado pra sincronizar o state após
// o usuário navegar via browser (back/forward, F5, link colado).
// O `lookups` permite que o componente recupere objetos completos (book, insight)
// a partir dos IDs na URL, usando os caches em memória.
export function parseUrlToView(
  pathname: string,
  lookups: {
    findBookById?: (id: string) => BookV2 | undefined;
    findInsightById?: (postId: string) => any | undefined;
    search?: string;
  } = {}
): { view: View; tab: Tab } {
  const path = pathname || '/';
  const search = lookups.search || (typeof window !== 'undefined' ? window.location.search : '');
  const sp = new URLSearchParams(search || '');

  // Tabs (raiz das 4 seções principais)
  if (path === '/' || path === '')      return { view: { kind: 'tab' }, tab: 'home' };
  if (path === '/grupos')               return { view: { kind: 'tab' }, tab: 'clubs' };
  if (path === '/ritmo')                return { view: { kind: 'tab' }, tab: 'stats' };
  if (path === '/explorar')             return { view: { kind: 'tab' }, tab: 'explore' };
  if (path === '/eu')                   return { view: { kind: 'tab' }, tab: 'me' };
  if (path === '/ajustes')              return { view: { kind: 'settings' } as any, tab: 'me' };
  if (path === '/tutoriais')            return { view: { kind: 'tutoriais' } as any, tab: 'me' };
  if (path === '/programa')             return { view: { kind: 'programa' } as any, tab: 'home' };
  {
    const mm = path.match(/^\/mentor\/([^/]+)\/?$/);
    if (mm) return { view: { kind: 'mentor', slug: decodeURIComponent(mm[1]).toLowerCase() } as any, tab: 'home' };
    const me = path.match(/^\/mentor\/([^/]+)\/estudio\/?$/);
    if (me) return { view: { kind: 'mentorEstudio', slug: decodeURIComponent(me[1]).toLowerCase() } as any, tab: 'home' };
    if (/^\/circulo\/editor\/?$/.test(path)) return { view: { kind: 'circuloEditor', ...(sp.get('vista') === 'serie' ? { vista: 'serie' } : {}) } as any, tab: 'home' };
  }
  if (path.startsWith('/u/'))            return { view: { kind: 'user', userId: decodeURIComponent(path.slice(3)) } as any, tab: 'me' };

  // Views simples sem parâmetro
  if (path === '/biblioteca')           return { view: { kind: 'library' }, tab: 'home' };
  if (path === '/relatorio')            return { view: { kind: 'report' },  tab: 'home' };
  if (path === '/notificacoes')         return { view: { kind: 'notif' },   tab: 'home' };
  if (path === '/estudos')              return { view: { kind: 'estudos' },  tab: 'me' };
  if (path === '/cadernos')             return { view: { kind: 'cadernos' }, tab: 'me' };
  if (path === '/admin')                return { view: { kind: 'admin' },   tab: 'me' };
  if (path === '/assinatura')           return { view: { kind: 'subscription' }, tab: 'me' };
  if (path === '/leituras')             return { view: { kind: 'readings' }, tab: 'me' };

  // Livro pessoal
  let m: RegExpMatchArray | null;
  if ((m = path.match(/^\/livro\/([^/]+)\/travessia\/?$/))) {
    const bookId = m[1];
    const book = lookups.findBookById?.(bookId);
    return { view: { kind: 'travessia', book: book || ({ id: bookId } as any), bookId }, tab: 'home' };
  }
  if ((m = path.match(/^\/livro\/([^/]+)\/caderno\/?$/))) {
    const bookId = m[1];
    const book = lookups.findBookById?.(bookId);
    return {
      view: { kind: 'book-study', book: book || ({ id: bookId } as any), bookId },
      tab: 'home',
    };
  }
  if ((m = path.match(/^\/livro\/([^/]+)\/?$/))) {
    const bookId = m[1];
    const book = lookups.findBookById?.(bookId);
    return {
      view: { kind: 'book', book: book || ({ id: bookId } as any), bookId },
      tab: 'home',
    };
  }

  // Explorar clubes públicos
  if (path === '/explorar')             return { view: { kind: 'club-explore' }, tab: 'clubs' };
  // Fluxo de criação de clube
  if (path === '/grupos/criar')         return { view: { kind: 'club-create' }, tab: 'clubs' };
  if (path === '/grupos/recem-criado')  return { view: { kind: 'club-empty', clubData: {}, clubId: null }, tab: 'clubs' };

  // Telas internas de clube
  if ((m = path.match(/^\/proposta\/([^/]+)\/?$/))) {
    return { view: { kind: 'proposta', clubId: m[1] }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/?$/))) {
    return { view: { kind: 'club-inner', clubId: m[1] }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/estudo\/?$/))) {
    const bookId = sp.get('book') || undefined;
    const focusPostId = sp.get('post') || undefined;
    return { view: { kind: 'estudo', clubId: m[1], bookId, focusPostId }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/painel\/?$/))) {
    return { view: { kind: 'club-analytics', clubId: m[1] }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/perguntas\/criar\/?$/))) {
    return { view: { kind: 'club-question-box', clubId: m[1], initialPhase: 'create' }, tab: 'clubs' };
  }
  // Caixa específica (admin) — postId é o id do post question-box
  if ((m = path.match(/^\/grupo\/([^/]+)\/perguntas\/([^/]+)\/?$/))) {
    return { view: { kind: 'club-question-box', clubId: m[1], initialPhase: 'active', postId: m[2] }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/perguntas\/?$/))) {
    return { view: { kind: 'club-question-box', clubId: m[1], initialPhase: 'active' }, tab: 'clubs' };
  }
  if ((m = path.match(/^\/grupo\/([^/]+)\/insight\/([^/]+)\/?$/))) {
    const clubId = m[1];
    const postId = m[2];
    const insight = lookups.findInsightById?.(postId);
    return {
      view: { kind: 'insight-detail', insight: insight || null, clubId, postId },
      tab: 'clubs',
    };
  }

  // Fallback: home. `desconhecida` deixa o App corrigir a URL (QA-15).
  return { view: { kind: 'tab' }, tab: 'home', desconhecida: path !== '/' && path !== '' } as any;
}
